'use client';

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faBullseyeArrow,
  faArrowTrendUp,
  faList,
  faMapLocationDot,
  faEllipsis,
  faDog,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faBolt,
  faFileExport,
  faGlobe,
  faFiles,
} from '@fortawesome/pro-regular-svg-icons';
import { useGetHexReportsQuery } from 'src/api/redux';
import { PrimaryNavItem, UserDropdown, MoreDropdown } from './components';
import { MoreMenuCallout } from './components/MoreMenuCallout';
import { useAuth } from 'src/context/auth';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { TARGET_STRATEGIES_READ } from 'src/components/PermissionGuard/checks';
import { GlobalSearchDialog } from '../GlobalSearch';
import { Page } from './types';
import { useOverflowNav } from './hooks/useOverflowNav';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { SCOUT_DRAWER_ID, SCOUT_PAGE_PATH } from 'src/components/MedScoutAI/constants';

const PrimaryNav = () => {
  const { hasPermission } = useAuthorization();
  const canAccessAi = hasPermission('read.ai.company');
  const pathname = usePathname();
  const { user } = useAuth();
  const { openDrawer } = useDrawer();

  const showHome =
    !!user?.has_strategies && hasPermission(TARGET_STRATEGIES_READ);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);

  const { data: globalReports, error: reportsError } = useGetHexReportsQuery({
    profile_type: 'global',
  });

  const reportsAvailable = !reportsError && globalReports?.results?.length > 0;
  const reportsDropdown = useMemo(() => {
    return globalReports?.results?.map((report) => ({
      path: `/global-reports/${report.id}`,
      label: report.label,
      show: true,
    }));
  }, [globalReports]);

  const pages: Page[] = useMemo(
    () =>
      [
        {
          path: '/',
          label: 'Strategies',
          show: showHome,
          icon: faBullseyeArrow,
          isBeta: true,
        },
        { path: '/search', label: 'Discovery', show: true, icon: faGlobe },
        { path: '/activity', label: 'Activity', show: true, icon: faBolt },
        { path: '/lists', label: 'Lists', show: true, icon: faList },
        {
          path: '/analytics',
          label: 'Analytics',
          icon: faArrowTrendUp,
          show: true,
        },
        {
          path: '/territory-manager',
          label: 'Territories',
          show: true,
          icon: faMapLocationDot,
        },
      ].filter((nav) => nav.show),
    [showHome]
  );

  // The Scout page shows its own chat, so the drawer doesn't open there.
  const openScoutDrawer = () => {
    if (pathname !== SCOUT_PAGE_PATH) openDrawer(SCOUT_DRAWER_ID);
  };
  const currentTab = pathname ? '/' + pathname.split('/')[1] : '/';

  const handleNavResize = useCallback(() => {
    setMoreMenuOpen(false);
  }, []);

  const overflowNavOptions = useMemo(
    () => ({ onResize: handleNavResize }),
    [handleNavResize]
  );

  const { visiblePages, overflowPages } = useOverflowNav(
    pages,
    navContainerRef,
    overflowNavOptions
  );

  const isPageActive = (page: Page): boolean => {
    if (!pathname) return false;

    // Special handling for Reports - check base path first
    if (
      page.path === '/global-reports' &&
      pathname.startsWith('/global-reports')
    ) {
      return true;
    }

    if (page.options) {
      return page.options.some((option) => {
        return pathname.startsWith(option.path);
      });
    }
    if (page.path === '/search') {
      return currentTab === '/search';
    }
    if (page.path === '/') {
      return currentTab === '/' || currentTab === '/strategy-center';
    }
    return currentTab === page.path;
  };

  const staticMorePages: Page[] = useMemo(
    () => [
      ...(reportsAvailable
        ? [
            {
              path: '/global-reports' as string | null,
              label: 'Reports',
              options: reportsDropdown,
              show: true,
              icon: faFiles,
            },
          ]
        : []),
      {
        path: '/exports',
        label: 'Exports',
        show: true,
        icon: faFileExport,
      },
    ],
    [reportsAvailable, reportsDropdown]
  );

  const allMorePages = useMemo(
    () => [...overflowPages, ...staticMorePages],
    [overflowPages, staticMorePages]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setGlobalSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <div
        className="w-14 self-stretch pt-3 pb-6 bg-sky-600 inline-flex 
      flex-col justify-between items-center overflow-hidden flex-shrink-0"
      >
        <div
          ref={navContainerRef}
          className="self-stretch flex-1 flex flex-col justify-start items-center gap-6 overflow-hidden"
        >
          <Link
            href="/"
            className="w-full flex flex-shrink-0 items-center justify-center rounded-md"
          >
            <Image
              src="/12_MedScout_Icon_White_RGB.svg"
              alt="MedScout"
              width={56}
              height={56}
              className="h-14 w-auto transition-all duration-300"
              data-testid="logo"
              priority
            />
          </Link>
          <div className="self-stretch py-2 flex flex-col justify-start items-center gap-4">
            {visiblePages.map((page) => {
              const isActive = isPageActive(page);
              const hasDropdown = page.options && page.options.length > 0;
              const targetPath =
                page.path || (page.options && page.options[0]?.path) || '/';

              const isReports = page.label === 'Reports';
              const dropdownOpen = isReports ? reportsMenuOpen : false;

              const handleClick = isReports
                ? () => setReportsMenuOpen(!reportsMenuOpen)
                : undefined;

              const handleClose = () => setReportsMenuOpen(false);

              return (
                <PrimaryNavItem
                  key={page.label}
                  label={page.label}
                  path={targetPath}
                  icon={page.icon}
                  isActive={isActive}
                  hasDropdown={hasDropdown}
                  onClick={hasDropdown ? handleClick : undefined}
                  dropdownOpen={dropdownOpen}
                  dropdownOptions={page.options || []}
                  onDropdownClose={handleClose}
                />
              );
            })}
            <div
              ref={moreButtonRef}
              className="flex flex-col justify-start items-center gap-0.5 cursor-pointer"
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              role="button"
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={moreMenuOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setMoreMenuOpen(!moreMenuOpen);
                }
              }}
            >
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-xl hover:bg-sky-700 ${
                  allMorePages.some((page) => page.path && isPageActive(page))
                    ? 'bg-sky-700'
                    : ''
                }`}
              >
                <Icon icon={faEllipsis} className="w-5 h-5 text-white" />
              </div>
              <div className="text-center text-white text-[9px] cursor-pointer font-semibold font-['Inter'] leading-3">
                More
              </div>
            </div>
          </div>
          <MoreDropdown
            containerRef={moreButtonRef}
            open={moreMenuOpen}
            onClose={() => setMoreMenuOpen(false)}
            pages={allMorePages}
          />
          {!moreMenuOpen && <MoreMenuCallout triggerRef={moreButtonRef} />}
        </div>
        <div className="h-1" />
        <div className="self-stretch py-2 inline-flex flex-col justify-end items-center gap-4">
          {canAccessAi && (
            <div
              className="w-11 inline-flex flex-col justify-start items-center gap-0.5 cursor-pointer"
              onClick={openScoutDrawer}
              role="button"
              tabIndex={0}
              aria-label="Open Scout AI"
              data-testid="primary-nav-ai-btn"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openScoutDrawer();
                }
              }}
            >
              <div className="w-10 h-10 rounded-xl hover:bg-sky-700 flex items-center justify-center">
                <Icon icon={faDog} size="lg" className="text-white" />
              </div>
              <div className="text-center text-white text-[9px] font-medium font-['Inter'] leading-3">
                Scout AI
              </div>
            </div>
          )}
          <div
            className="w-11 inline-flex flex-col justify-start items-center gap-0.5 cursor-pointer"
            onClick={() => setGlobalSearchOpen(true)}
            role="button"
            tabIndex={0}
            aria-label="Open Global Search"
            data-testid="primary-nav-global-search-btn"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setGlobalSearchOpen(true);
              }
            }}
          >
            <div className="w-10 h-10 rounded-xl hover:bg-sky-700 flex items-center justify-center">
              <Icon icon={faSearch} className="w-5 h-5 text-white" />
            </div>
            <div className="text-center text-white text-[9px] font-medium font-['Inter'] leading-3">
              Search
            </div>
          </div>
          <UserDropdown />
        </div>
      </div>
      <GlobalSearchDialog
        open={globalSearchOpen}
        onClose={() => setGlobalSearchOpen(false)}
      />
    </>
  );
};

export default PrimaryNav;
